import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/addons/utils/SkeletonUtils.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { BUILDINGS, TROOPS } from './state.js';
import { GAME_RULES, targetScore, villageVisualStage } from './rules.js';
import { assetUrl } from './assets.js';
import { BIOMES, CITY_PROPS, EQUIPMENT, MONSTERS, PROJECTILES } from './content.js';

export class StampWorld {
  constructor(canvas, hooks = {}) {
    this.canvas = canvas;
    this.hooks = hooks;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#8edff0');
    this.scene.fog = new THREE.FogExp2('#bcebf1', .012);
    this.camera = new THREE.PerspectiveCamera(38, 1, .1, 220);
    this.camera.position.set(25, 25, 27);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = .08;
    this.controls.enablePan = false;
    this.controls.minDistance = 15;
    this.controls.maxDistance = 54;
    this.controls.maxPolarAngle = Math.PI * .44;
    this.controls.minPolarAngle = Math.PI * .19;
    this.controls.target.set(0, -1.35, 0);
    this.clock = new THREE.Clock();
    this.loader = new GLTFLoader();
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    this.cache = new Map();
    this.mixers = [];
    this.mode = 'city';
    this.worldRoot = new THREE.Group();
    this.scene.add(this.worldRoot);
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.selectedBuilding = null;
    this.movingBuilding = null;
    this.highlight = null;
    this.battle = null;
    this.battleAccumulator = 0;
    this.pointerStart = new THREE.Vector2();
    this.battleSequence = 0;
    this.projectileSequence = 0;
    this.ambientObjects = [];
    this.villagers = [];
    this.shakeStrength = 0;
    this.shakeUntil = 0;
    this.slowMotionUntil = 0;
    this.lastMood = '';
    this.qualityPreference = 'auto';
    this.qualityLevel = 'high';
    this.frameSamples = [];
    this.setupLights();
    this.setupClouds();
    this.bindEvents();
    this.resize();
    this.animate();
  }

  setupLights() {
    this.hemi = new THREE.HemisphereLight('#fff9dd', '#7b9c8e', 2.15);
    this.scene.add(this.hemi);
    const sun = new THREE.DirectionalLight('#fff4dc', 3.4);
    sun.position.set(-18, 32, 20);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -42;
    sun.shadow.camera.right = 42;
    sun.shadow.camera.top = 42;
    sun.shadow.camera.bottom = -42;
    sun.shadow.bias = -.0004;
    sun.shadow.normalBias = .025;
    this.scene.add(sun);
    this.sun = sun;
    const rim = new THREE.DirectionalLight('#63dffc', 1.2);
    rim.position.set(25, 12, -22);
    this.scene.add(rim);
    this.rim = rim;
  }

  setupClouds() {
    const cloudMaterial = new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: .38, depthWrite: false });
    for (let i = 0; i < 20; i += 1) {
      const cloud = new THREE.Mesh(new THREE.SphereGeometry(2.8 + Math.random() * 3, 18, 12), cloudMaterial);
      cloud.scale.y = .25;
      cloud.position.set((Math.random() - .5) * 100, -7 - Math.random() * 7, (Math.random() - .5) * 90);
      cloud.userData.drift = .25 + Math.random() * .35;
      cloud.userData.cloud = true;
      this.scene.add(cloud);
    }
  }

  bindEvents() {
    window.addEventListener('resize', () => this.resize());
    this.canvas.addEventListener('pointerdown', event => this.pointerStart.set(event.clientX, event.clientY));
    this.canvas.addEventListener('pointerup', event => {
      if (this.pointerStart.distanceTo(new THREE.Vector2(event.clientX, event.clientY)) > 8) return;
      this.pointer.x = (event.clientX / this.canvas.clientWidth) * 2 - 1;
      this.pointer.y = -(event.clientY / this.canvas.clientHeight) * 2 + 1;
      this.raycaster.setFromCamera(this.pointer, this.camera);
      if (this.mode === 'battle') {
        const ground = this.worldRoot.getObjectByName('Deployment Ground');
        if (!ground) return;
        const hit = this.raycaster.intersectObject(ground, false)[0];
        if (hit) this.hooks.onBattleGround?.(hit.point);
        return;
      }
      if (this.movingBuilding) {
        const ground = this.worldRoot.getObjectByName('Deployment Ground');
        const groundHit = ground && this.raycaster.intersectObject(ground, false)[0];
        if (groundHit) this.hooks.onBuildingMove?.(this.movingBuilding.userData.state, groundHit.point);
        this.movingBuilding = null;
        return;
      }
      const hits = this.raycaster.intersectObjects(this.worldRoot.children, true);
      const hit = hits.find(entry => this.findBuilding(entry.object));
      if (hit) {
        const building = this.findBuilding(hit.object);
        this.selectBuilding(building);
        this.hooks.onBuildingSelected?.(building.userData.state);
      } else {
        this.clearSelection();
        this.hooks.onBuildingSelected?.(null);
      }
    });
  }

  findBuilding(object) {
    let current = object;
    while (current && current !== this.worldRoot) {
      if (current.userData.buildingRoot) return current;
      current = current.parent;
    }
    return null;
  }

  resize() {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  setQuality(preference = 'auto') {
    this.qualityPreference = ['auto', 'low', 'medium', 'high'].includes(preference) ? preference : 'auto';
    const level = this.qualityPreference === 'auto' ? this.qualityLevel : this.qualityPreference;
    this.qualityLevel = level;
    const settings = {
      low: { pixelRatio: 1, shadows: false, shadowSize: 512 },
      medium: { pixelRatio: 1.3, shadows: true, shadowSize: 1024 },
      high: { pixelRatio: 1.75, shadows: true, shadowSize: 2048 }
    }[level];
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.pixelRatio));
    this.renderer.shadowMap.enabled = settings.shadows;
    this.sun.shadow.mapSize.set(settings.shadowSize, settings.shadowSize);
    this.resize();
    return level;
  }

  updateAdaptiveQuality(delta) {
    if (this.qualityPreference !== 'auto') return;
    this.frameSamples.push(delta);
    if (this.frameSamples.length < 120) return;
    const average = this.frameSamples.reduce((sum, value) => sum + value, 0) / this.frameSamples.length;
    this.frameSamples.length = 0;
    if (average > .03 && this.qualityLevel !== 'low') {
      this.qualityLevel = this.qualityLevel === 'high' ? 'medium' : 'low';
      this.setQuality('auto');
      this.hooks.onQualityChange?.(this.qualityLevel);
    } else if (average < .018 && this.qualityLevel === 'medium') {
      this.qualityLevel = 'high';
      this.setQuality('auto');
      this.hooks.onQualityChange?.(this.qualityLevel);
    }
  }

  async loadGLB(url, onProgress) {
    if (!this.cache.has(url)) {
      this.cache.set(url, new Promise((resolve, reject) => {
        this.loader.load(url, resolve, event => {
          if (event.total) onProgress?.(event.loaded / event.total);
        }, reject);
      }));
    }
    return this.cache.get(url);
  }

  async model(url, targetSize, options = {}) {
    try {
      const gltf = await this.loadGLB(url, options.onProgress);
      const content = skeletonClone(gltf.scene);
      const hasAuthoredMaterials = Boolean(gltf.parser?.json?.materials?.length);
      if (options.rotateX) content.rotation.x = options.rotateX;
      content.updateMatrixWorld(true);
      let bounds = new THREE.Box3().setFromObject(content);
      const size = bounds.getSize(new THREE.Vector3());
      const denominator = options.fitHeight ? size.y : Math.max(size.x, size.z);
      const scale = targetSize / Math.max(.001, denominator);
      content.scale.setScalar(scale);
      content.updateMatrixWorld(true);
      bounds = new THREE.Box3().setFromObject(content);
      const center = bounds.getCenter(new THREE.Vector3());
      content.position.x -= center.x;
      content.position.z -= center.z;
      content.position.y -= options.alignTop ? bounds.max.y : bounds.min.y;
      content.traverse(node => {
        if (!node.isMesh) return;
        node.castShadow = true;
        node.receiveShadow = true;
        if (!node.material) node.material = new THREE.MeshStandardMaterial();
        const materials = (Array.isArray(node.material) ? node.material : [node.material]).map(material => material.clone());
        for (const material of materials) {
          if (!hasAuthoredMaterials && options.color && material.color) material.color.set(options.color);
          for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap']) {
            if (material[key]) material[key].anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
          }
        }
        node.material = Array.isArray(node.material) ? materials : materials[0];
      });
      const wrapper = new THREE.Group();
      wrapper.add(content);
      content.userData.motionBasePosition = content.position.clone();
      content.userData.motionBaseScale = content.scale.clone();
      if (gltf.animations?.length) {
        const mixer = new THREE.AnimationMixer(content);
        const actions = new Map(gltf.animations.map(clip => [clip.name.toLowerCase(), mixer.clipAction(clip)]));
        wrapper.userData.animation = { mixer, actions, current: '' };
        this.mixers.push(mixer);
      }
      return wrapper;
    } catch (error) {
      console.warn(`Asset failed: ${url}`, error);
      return this.fallbackModel(targetSize, options.color || '#65b7a2', options.fitHeight);
    }
  }

  fallbackModel(size, color, tall = false) {
    const group = new THREE.Group();
    const body = new THREE.Mesh(
      tall ? new THREE.CapsuleGeometry(size * .22, size * .45, 6, 12) : new THREE.CylinderGeometry(size * .34, size * .45, size * .58, 8),
      new THREE.MeshStandardMaterial({ color, roughness: .72 })
    );
    body.position.y = tall ? size * .46 : size * .29;
    body.castShadow = true;
    group.add(body);
    return group;
  }

  clearWorld() {
    this.clearSelection();
    this.mixers = [];
    this.worldRoot.clear();
    this.battle = null;
    this.movingBuilding = null;
    this.ambientObjects = [];
    this.villagers = [];
  }

  async addIsland(position, width, name) {
    const island = this.createIslandShell(width, name);
    island.position.copy(position);
    island.name = name;
    this.worldRoot.add(island);
    return island;
  }

  createIslandShell(width, name = '') {
    const group = new THREE.Group();
    const segments = Math.max(24, Math.round(width));
    const radius = width * .5;
    const seed = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 19);
    const points = Array.from({ length: segments }, (_, index) => {
      const angle = index / segments * Math.PI * 2;
      const variation = 1 + Math.sin(angle * 3 + seed * .17) * .085 + Math.sin(angle * 7 - seed * .11) * .04;
      return new THREE.Vector2(Math.cos(angle) * radius * variation, Math.sin(angle) * radius * .86 * variation);
    });

    const topPositions = [0, .015, 0];
    const topColors = [];
    const centerColor = new THREE.Color('#6fa356');
    topColors.push(centerColor.r, centerColor.g, centerColor.b);
    for (let index = 0; index < segments; index += 1) {
      const point = points[index];
      topPositions.push(point.x, 0, point.y);
      const color = new THREE.Color(index % 3 ? '#679c53' : '#75a95b');
      topColors.push(color.r, color.g, color.b);
    }
    const topIndices = [];
    for (let index = 0; index < segments; index += 1) topIndices.push(0, (index + 1) % segments + 1, index + 1);
    const topGeometry = new THREE.BufferGeometry();
    topGeometry.setAttribute('position', new THREE.Float32BufferAttribute(topPositions, 3));
    topGeometry.setAttribute('color', new THREE.Float32BufferAttribute(topColors, 3));
    topGeometry.setIndex(topIndices);
    topGeometry.computeVertexNormals();
    const top = new THREE.Mesh(topGeometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .96, metalness: 0 }));
    top.receiveShadow = true;
    top.name = 'Island Grass';
    group.add(top);

    const edgeGeometry = new THREE.BufferGeometry().setFromPoints(points.map(point => new THREE.Vector3(point.x, .045, point.y)));
    const edge = new THREE.LineLoop(edgeGeometry, new THREE.LineBasicMaterial({ color: '#d6dda0', transparent: true, opacity: .72 }));
    edge.name = 'Grass Edge';
    group.add(edge);

    const rings = [
      { scale: 1, y: -.02, color: '#557c49' },
      { scale: 1.035, y: -.42, color: '#726d4e' },
      { scale: .98, y: -2.45, color: '#685b4c' },
      { scale: .68, y: -5.3, color: '#5b5160' },
      { scale: .22, y: -8.15, color: '#494758' },
      { scale: .07, y: -9.15, color: '#3c3c49' }
    ];
    const sidePositions = [];
    const sideColors = [];
    const sideIndices = [];
    for (let ringIndex = 0; ringIndex < rings.length; ringIndex += 1) {
      const ring = rings[ringIndex];
      const color = new THREE.Color(ring.color);
      for (let index = 0; index < segments; index += 1) {
        const point = points[index];
        const ridge = ringIndex > 1 ? 1 + Math.sin(index * 4.31 + seed) * .035 : 1;
        sidePositions.push(point.x * ring.scale * ridge, ring.y, point.y * ring.scale * ridge);
        const shade = color.clone().offsetHSL(0, 0, ((index + ringIndex) % 4 - 1.5) * .018);
        sideColors.push(shade.r, shade.g, shade.b);
      }
    }
    for (let ringIndex = 0; ringIndex < rings.length - 1; ringIndex += 1) {
      for (let index = 0; index < segments; index += 1) {
        const next = (index + 1) % segments;
        const a = ringIndex * segments + index;
        const b = ringIndex * segments + next;
        const c = (ringIndex + 1) * segments + next;
        const d = (ringIndex + 1) * segments + index;
        sideIndices.push(a, b, c, a, c, d);
      }
    }
    const sideGeometry = new THREE.BufferGeometry();
    sideGeometry.setAttribute('position', new THREE.Float32BufferAttribute(sidePositions, 3));
    sideGeometry.setAttribute('color', new THREE.Float32BufferAttribute(sideColors, 3));
    sideGeometry.setIndex(sideIndices);
    sideGeometry.computeVertexNormals();
    const cliff = new THREE.Mesh(sideGeometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .9, flatShading: true, side: THREE.DoubleSide }));
    cliff.castShadow = true;
    cliff.receiveShadow = true;
    cliff.name = 'Island Cliff';
    group.add(cliff);

    const coreMaterial = new THREE.MeshStandardMaterial({ color: '#504c59', roughness: .94, flatShading: true });
    const core = new THREE.Mesh(new THREE.ConeGeometry(radius * .55, 8.6, 11, 3), coreMaterial);
    core.rotation.z = Math.PI;
    core.position.y = -4.3;
    core.castShadow = true;
    core.receiveShadow = true;
    core.name = 'Island Stone Core';
    group.add(core);
    for (let index = 0; index < 5; index += 1) {
      const angle = index / 5 * Math.PI * 2 + seed * .14;
      const height = 3.4 + (index % 3) * .65;
      const fang = new THREE.Mesh(new THREE.ConeGeometry(.55 + (index % 2) * .22, height, 5), coreMaterial);
      fang.rotation.z = Math.PI;
      fang.position.set(Math.cos(angle) * radius * .4, -height * .5 - .65, Math.sin(angle) * radius * .32);
      fang.rotation.y = angle;
      fang.castShadow = true;
      group.add(fang);
    }

    const plaza = new THREE.Mesh(
      new THREE.CircleGeometry(Math.min(3.45, radius * .28), 32),
      new THREE.MeshBasicMaterial({ color: '#d4c996', transparent: true, opacity: .22, side: THREE.DoubleSide, depthWrite: false })
    );
    plaza.rotation.x = -Math.PI / 2;
    plaza.position.y = .023;
    group.add(plaza);
    const plazaRim = new THREE.Mesh(
      new THREE.RingGeometry(Math.min(3.48, radius * .285), Math.min(3.72, radius * .305), 32),
      new THREE.MeshBasicMaterial({ color: '#e0d7a4', transparent: true, opacity: .4, side: THREE.DoubleSide, depthWrite: false })
    );
    plazaRim.rotation.x = -Math.PI / 2;
    plazaRim.position.y = .026;
    group.add(plazaRim);

    const patchMaterial = new THREE.MeshBasicMaterial({ color: '#447e43', transparent: true, opacity: .1, depthWrite: false, side: THREE.DoubleSide });
    for (let index = 0; index < 6; index += 1) {
      const angle = index / 6 * Math.PI * 2 + seed * .06;
      const patch = new THREE.Mesh(new THREE.CircleGeometry(.72 + (index % 3) * .22, 9), patchMaterial);
      patch.rotation.x = -Math.PI / 2;
      patch.scale.y = .58 + (index % 2) * .17;
      patch.rotation.z = angle * .7;
      patch.position.set(Math.cos(angle) * radius * .48, .022, Math.sin(angle) * radius * .39);
      group.add(patch);
    }

    const topRockMaterial = new THREE.MeshStandardMaterial({ color: '#76806d', roughness: .98, flatShading: true });
    for (let index = 0; index < Math.min(8, Math.round(width * .3)); index += 1) {
      const angle = index / Math.min(8, Math.round(width * .3)) * Math.PI * 2 + seed * .19;
      const size = .22 + (index % 4) * .08;
      const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(size, 0), topRockMaterial);
      rock.scale.set(1.25, .7, .9);
      rock.position.set(Math.cos(angle) * radius * (.58 + (index % 2) * .12), size * .36, Math.sin(angle) * radius * .86 * (.58 + (index % 2) * .12));
      rock.rotation.set(index * .2, angle, index * .13);
      rock.castShadow = true;
      rock.receiveShadow = true;
      group.add(rock);
    }

    const grassMaterial = new THREE.MeshStandardMaterial({ color: '#477b46', roughness: 1, side: THREE.DoubleSide });
    for (let index = 0; index < Math.min(18, Math.round(width * .7)); index += 1) {
      const angle = index / Math.min(18, Math.round(width * .7)) * Math.PI * 2 + seed * .13;
      const tuft = new THREE.Mesh(new THREE.ConeGeometry(.08 + (index % 3) * .025, .32 + (index % 4) * .04, 3), grassMaterial);
      tuft.position.set(Math.cos(angle) * radius * (.7 + (index % 3) * .07), .16, Math.sin(angle) * radius * .86 * (.7 + (index % 3) * .07));
      tuft.rotation.z = Math.sin(angle) * .13;
      tuft.castShadow = true;
      group.add(tuft);
    }

    const fragmentMaterial = new THREE.MeshStandardMaterial({ color: '#687064', roughness: .92, flatShading: true });
    for (let index = 0; index < Math.min(12, Math.round(width * .45)); index += 1) {
      const angle = index / Math.min(12, Math.round(width * .45)) * Math.PI * 2 + seed * .09;
      const size = .18 + ((index * 17 + seed) % 7) * .065;
      const fragment = new THREE.Mesh(new THREE.IcosahedronGeometry(size, 0), fragmentMaterial);
      fragment.position.set(Math.cos(angle) * radius * (.7 + (index % 3) * .11), -1.5 - (index % 5) * .82, Math.sin(angle) * radius * .86 * (.7 + (index % 3) * .11));
      fragment.rotation.set(index * .41, index * .73, index * .29);
      fragment.castShadow = true;
      group.add(fragment);
    }
    return group;
  }

  async addEnvironmentModel(name, position, size, rotation = 0) {
    const object = await this.model(assetUrl.module(name), size, { color: '#7fac86' });
    object.position.copy(position);
    object.rotation.y = rotation;
    this.worldRoot.add(object);
    this.registerAmbient(object, name);
    return object;
  }

  async addPropModel(name, position, size, rotation = 0) {
    const object = await this.model(assetUrl.prop(name), size, { color: '#7fac86' });
    object.position.copy(position);
    object.rotation.y = rotation;
    this.worldRoot.add(object);
    this.registerAmbient(object, name);
    return object;
  }

  registerAmbient(object, name) {
    const lower = name.toLowerCase();
    const type = /fountain|well|pond|water/.test(lower) ? 'water'
      : /banner|flag/.test(lower) ? 'banner'
        : /crystal|portal|shrine|totem|lantern/.test(lower) ? 'magic'
          : /tree|grove|plant/.test(lower) ? 'foliage' : 'idle';
    object.userData.ambient = { type, baseY: object.position.y, baseRotation: object.rotation.y, phase: Math.random() * Math.PI * 2 };
    this.ambientObjects.push(object);
    if (type === 'magic') {
      const glow = new THREE.PointLight('#55eee8', .55, 5.5);
      glow.position.set(0, 1.25, 0);
      glow.userData.ambientGlow = true;
      object.add(glow);
    }
  }

  async addCatalogModel(url, position, size, options = {}) {
    const object = await this.model(url, size, options);
    object.position.copy(position);
    object.rotation.y = options.rotation || 0;
    this.worldRoot.add(object);
    return object;
  }

  async showCity(state, progress = () => {}) {
    this.mode = 'city';
    this.clearWorld();
    this.scene.fog.density = .012;
    this.camera.position.set(25 + state.territoryLevel * 4, 25 + state.territoryLevel * 2, 27);
    this.controls.target.set(state.territoryLevel ? 8 : 0, -1.35, state.territoryLevel >= 3 ? 2 : 0);
    this.controls.update();
    const visualStage = villageVisualStage(state.buildings);
    const tasks = [this.addIsland(new THREE.Vector3(0, 0, 0), 27, 'เกาะเริ่มต้น')];
    if (state.territoryLevel >= 1) {
      tasks.push(this.addIsland(new THREE.Vector3(20, 0, 0), 17, 'เกาะสายลม'));
      tasks.push(this.addEnvironmentModel('ENV_StoneBridge_A.glb', new THREE.Vector3(10, -.15, 0), 11, Math.PI / 2));
    }
    if (state.territoryLevel >= 3) {
      tasks.push(this.addIsland(new THREE.Vector3(28, .8, 10), 12, 'ยอดผาคริสตัล'));
      tasks.push(this.addEnvironmentModel('ENV_RopeBridge_A.glb', new THREE.Vector3(24.2, .15, 5.5), 8, Math.PI * .23));
      tasks.push(this.addEnvironmentModel('ENV_StormCrystalSpire_A.glb', new THREE.Vector3(29, .6, 11), 3.1, -.4));
    }
    const naturalScenery = [
      ['ENV_SpiritTree_A.glb', -8.4, 5.8, 3.8, .35],
      ['ENV_CloudstoneBoulder_A.glb', 7.9, 5.9, 1.8, -.5],
      ['ENV_LotusPondPatch_A.glb', -7.8, -6.4, 2.9, .15]
    ];
    for (const [file, x, z, size, rotation] of naturalScenery) {
      if (this.isScenerySpotClear(state, x, z, size * .45 + .7)) tasks.push(this.addEnvironmentModel(file, new THREE.Vector3(x, .025, z), size, rotation));
    }
    // Visual life follows completed construction one step at a time. In particular,
    // completing/skipping the tutorial must never populate an otherwise empty base.
    if (visualStage >= 1) {
      if (this.isScenerySpotClear(state, -9.4, 7.7, 2)) tasks.push(this.addPropModel('ENV_SmallShrine_A.glb', new THREE.Vector3(-9.4, .025, 7.7), 2.2, .4));
    }
    if (visualStage >= 2) {
      if (this.isScenerySpotClear(state, 7.4, 7, 1.9)) tasks.push(this.addPropModel('ENV_CloudFountain_A.glb', new THREE.Vector3(7.4, .025, 7), 2.8, -.4));
      if (this.isScenerySpotClear(state, -8, -6.7, 1.65)) tasks.push(this.addPropModel('ENV_VillageBanner_A.glb', new THREE.Vector3(-8, .025, -6.7), 2.3, .2));
    }
    if (visualStage >= 3) {
      if (this.isScenerySpotClear(state, 8, -6.7, 2.1)) tasks.push(this.addPropModel('ENV_MarketStall_A.glb', new THREE.Vector3(8, .025, -6.7), 3.2, -.3));
      tasks.push(...this.cityDetailTasks(state, visualStage));
    }
    let loaded = 0;
    for (const buildingState of state.buildings) {
      const task = this.addBuilding(buildingState).then(() => progress(++loaded / state.buildings.length));
      tasks.push(task);
    }
    await Promise.all(tasks);
    this.addCityPaths(state);
    if (visualStage >= 1) await this.addAmbientLife(state, visualStage);
    this.addGround(48, false);
  }

  isScenerySpotClear(state, x, z, clearance = 1.5) {
    return state.buildings.every(building => {
      const footprint = BUILDINGS[building.type]?.size || 3;
      return Math.hypot(building.x - x, building.z - z) > footprint * .48 + clearance;
    });
  }

  addCityPaths(state) {
    const material = new THREE.MeshStandardMaterial({ color: '#d2c38e', roughness: 1, transparent: true, opacity: .72, polygonOffset: true, polygonOffsetFactor: -2 });
    for (const building of state.buildings.filter(item => !item.underConstruction)) {
      const center = building.x > 24 && building.z > 4 ? new THREE.Vector2(28, 10) : building.x > 13 ? new THREE.Vector2(20, 0) : new THREE.Vector2(0, 0);
      const end = new THREE.Vector2(building.x, building.z);
      const direction = end.clone().sub(center);
      const distance = direction.length();
      if (distance < 2.4 || distance > 12.5) continue;
      direction.normalize();
      const start = center.clone().addScaledVector(direction, 1.5);
      const target = end.clone().addScaledVector(direction, -Math.min(1.2, distance * .16));
      const normal = new THREE.Vector2(-direction.y, direction.x).multiplyScalar(.38);
      const positions = [
        start.x + normal.x, .028, start.y + normal.y,
        start.x - normal.x, .028, start.y - normal.y,
        target.x - normal.x, .028, target.y - normal.y,
        target.x + normal.x, .028, target.y + normal.y
      ];
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setIndex([0, 1, 2, 0, 2, 3]);
      geometry.computeVertexNormals();
      const path = new THREE.Mesh(geometry, material);
      path.receiveShadow = true;
      path.name = 'Village Path';
      this.worldRoot.add(path);
    }
  }

  cityDetailTasks(state, visualStage) {
    const positions = [
      [-10, 7], [-10, -6], [10, 7], [10, -6], [-7, 10], [7, 10], [-7, -9], [7, -9],
      [15, 7], [15, -7], [20, 7], [20, -7], [25, 5], [25, -5], [29, 8], [31, 13],
      [27, 14], [35, 12], [33, 7], [18, 10], [-15, 14], [-15, -14], [12, 16], [12, -16],
      [22, 15], [22, -15], [30, -10], [37, 4], [38, 16], [25, 20], [31, 22], [41, 10]
    ];
    const visibleCount = Math.min(Math.max(0, CITY_PROPS.length - 3), Math.max(0, visualStage - 2) * 2 + state.territoryLevel * 5);
    return CITY_PROPS.slice(3, 3 + visibleCount).map((name, index) => {
      const [x, z] = positions[index % positions.length];
      const size = name.includes('Node') || name.includes('Portal') ? 2.2 : 1.35;
      if (!this.isScenerySpotClear(state, x, z, size * .45 + .55)) return null;
      return this.addPropModel(name, new THREE.Vector3(x, .025, z), size, index * .71);
    }).filter(Boolean);
  }

  async addAmbientLife(state, visualStage) {
    const spiritMaterial = new THREE.MeshBasicMaterial({ color: '#8affef', transparent: true, opacity: .82, depthWrite: false });
    const count = Math.min(9, visualStage + state.territoryLevel);
    for (let index = 0; index < count; index += 1) {
      const spirit = new THREE.Mesh(new THREE.SphereGeometry(.1 + (index % 3) * .025, 8, 8), spiritMaterial.clone());
      const radius = 5.5 + (index % 4) * 2.3;
      const angle = index / count * Math.PI * 2;
      spirit.position.set(Math.cos(angle) * radius, 1.2 + (index % 3) * .35, Math.sin(angle) * radius);
      spirit.userData.ambient = { type: 'spirit', phase: angle, radius, centerX: 0, centerZ: 0, baseY: spirit.position.y };
      const light = new THREE.PointLight('#61f5ea', .22, 2.4);
      spirit.add(light);
      this.worldRoot.add(spirit);
      this.ambientObjects.push(spirit);
    }

    const villagerCount = Math.min(4, Math.floor(visualStage / 2) + state.territoryLevel);
    const models = ['CloudRunner_Rigged.glb', 'LotusKeeper_Rigged.glb', 'GearwingTinkerer_Rigged.glb', 'CrosswindCaptain_Rigged.glb'];
    for (let index = 0; index < villagerCount; index += 1) {
      const villager = await this.model(assetUrl.character(models[index]), 1.45, { fitHeight: true, color: '#d8a05c' });
      const angle = index / villagerCount * Math.PI * 2;
      villager.position.set(Math.cos(angle) * (4.2 + index), .035, Math.sin(angle) * (4.2 + index));
      villager.userData.villager = { phase: angle, radius: 4.2 + index * 1.25, speed: .08 + index * .012, centerX: 0, centerZ: 0 };
      villager.userData.motionState = 'run';
      this.playModelAnimation(villager, 'run');
      this.worldRoot.add(villager);
      this.villagers.push(villager);
    }
  }

  async addBuilding(buildingState, battle = false) {
    const def = BUILDINGS[buildingState.type];
    const root = await this.model(assetUrl.building(def.model), def.size, { color: '#d0a34e' });
    root.position.set(buildingState.x, .045, buildingState.z);
    root.rotation.y = Number.isFinite(buildingState.rotation) ? buildingState.rotation * Math.PI / 2 : ((buildingState.x + buildingState.z) * .09) % .45;
    root.scale.setScalar(1 + (buildingState.level - 1) * .075);
    root.userData.buildingRoot = true;
    root.userData.state = buildingState;
    root.userData.definition = def;
    this.addBuildingFoundation(root, def, battle);
    if (battle) {
      root.userData.maxHp = def.hp * (.8 + buildingState.level * .3);
      root.userData.hp = root.userData.maxHp;
      root.userData.alive = true;
    }
    this.applyLevelVisuals(root, buildingState.level, def.size);
    if (!battle && buildingState.underConstruction) this.applyConstructionVisual(root, def.size);
    root.traverse(child => { child.userData.buildingOwner = root; });
    this.worldRoot.add(root);
    if (!battle && buildingState.type === 'workshop') this.addWorkshopDisplay(root, buildingState.level).catch(() => {});
    return root;
  }

  addBuildingFoundation(root, def, battle = false) {
    const radius = Math.max(.78, def.size * .5);
    const foundation = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * .92, radius, .13, 12),
      new THREE.MeshStandardMaterial({ color: battle ? '#767064' : '#8d8c6d', roughness: .92, metalness: 0 })
    );
    foundation.position.y = -.02;
    foundation.receiveShadow = true;
    foundation.castShadow = true;
    foundation.name = 'Building Foundation';
    root.add(foundation);
    const contact = new THREE.Mesh(
      new THREE.CircleGeometry(radius * .88, 24),
      new THREE.MeshBasicMaterial({ color: '#17251d', transparent: true, opacity: .16, depthWrite: false, side: THREE.DoubleSide })
    );
    contact.rotation.x = -Math.PI / 2;
    contact.position.y = -.034;
    contact.name = 'Contact Shadow';
    root.add(contact);
  }

  applyConstructionVisual(root, size) {
    const cage = new THREE.Mesh(
      new THREE.BoxGeometry(size * .9, size * .75, size * .9),
      new THREE.MeshBasicMaterial({ color: '#ffd56a', wireframe: true, transparent: true, opacity: .55 })
    );
    cage.name = 'Construction Cage';
    cage.position.y = size * .38;
    root.add(cage);
  }

  async addWorkshopDisplay(root, level) {
    const amount = Math.min(EQUIPMENT.length, 4 + level * 4);
    for (let index = 0; index < amount; index += 1) {
      const [kind, file] = EQUIPMENT[index];
      const url = kind === 'weapon' ? assetUrl.weapon(file) : kind === 'armor' ? assetUrl.armor(file) : assetUrl.item(file);
      const item = await this.model(url, .65, { color: '#e8b74f' });
      const angle = index / amount * Math.PI * 2;
      item.position.set(Math.cos(angle) * 2.2, .15, Math.sin(angle) * 2.2);
      item.rotation.y = -angle;
      root.add(item);
    }
  }

  addGround(size, visible = false) {
    const material = new THREE.MeshBasicMaterial({ color: '#78b86c', transparent: true, opacity: visible ? .16 : 0, side: THREE.DoubleSide, depthWrite: false });
    const ground = new THREE.Mesh(new THREE.CircleGeometry(size, 64), material);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = .04;
    ground.name = 'Deployment Ground';
    this.worldRoot.add(ground);
    return ground;
  }

  selectBuilding(root) {
    this.clearSelection();
    this.selectedBuilding = root;
    const def = root.userData.definition;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(def.size * .58, def.size * .69, 48),
      new THREE.MeshBasicMaterial({ color: '#2efff1', side: THREE.DoubleSide, transparent: true, opacity: .9 })
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.copy(root.position);
    ring.position.y = .075;
    ring.userData.highlight = true;
    this.worldRoot.add(ring);
    this.highlight = ring;
  }

  clearSelection() {
    if (this.highlight) this.worldRoot.remove(this.highlight);
    this.highlight = null;
    this.selectedBuilding = null;
  }

  beginMove(buildingId) {
    const root = this.worldRoot.children.find(child => child.userData?.state?.id === buildingId);
    if (!root) return false;
    this.movingBuilding = root;
    this.clearSelection();
    return true;
  }

  refreshBuilding(buildingState) {
    const root = this.worldRoot.children.find(child => child.userData?.state?.id === buildingState.id);
    if (!root) return;
    root.scale.setScalar(1 + (buildingState.level - 1) * .075);
    this.applyLevelVisuals(root, buildingState.level, root.userData.definition.size);
    this.selectBuilding(root);
  }

  applyLevelVisuals(root, level, size) {
    const previous = root.getObjectByName('Level Visuals');
    if (previous) root.remove(previous);
    if (level <= 1) return;
    const visuals = new THREE.Group();
    visuals.name = 'Level Visuals';
    const gold = new THREE.MeshStandardMaterial({ color: '#f8c74c', metalness: .45, roughness: .32 });
    const spirit = new THREE.MeshStandardMaterial({ color: '#45f1ef', emissive: '#087b91', emissiveIntensity: 1.1, roughness: .25 });
    const stone = new THREE.MeshStandardMaterial({ color: '#d8d2ba', roughness: .8 });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(size * .48, size * .52, .16, 12), level >= 3 ? gold : stone);
    base.position.y = .08;
    base.receiveShadow = true;
    visuals.add(base);
    if (level >= 2) {
      for (const [x, z] of [[-1,-1], [1,-1], [-1,1], [1,1]]) {
        const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(.16 + level * .018), spirit);
        crystal.position.set(x * size * .44, .52, z * size * .4);
        const glow = new THREE.PointLight('#54f7ff', .45, size * 1.4);
        glow.position.copy(crystal.position);
        visuals.add(crystal);
        visuals.add(glow);
      }
    }
    if (level >= 3) {
      for (const [x, z] of [[-1,-1], [1,-1], [-1,1], [1,1]]) {
        const pillar = new THREE.Mesh(new THREE.CylinderGeometry(.1, .14, .72, 6), gold);
        pillar.position.set(x * size * .4, .36, z * size * .36);
        pillar.castShadow = true;
        const cap = new THREE.Mesh(new THREE.OctahedronGeometry(.14), spirit);
        cap.position.copy(pillar.position).add(new THREE.Vector3(0, .45, 0));
        visuals.add(pillar, cap);
      }
    }
    if (level >= 4) {
      const halo = new THREE.Mesh(new THREE.TorusGeometry(size * .45, .055, 10, 64), spirit);
      halo.rotation.x = Math.PI / 2;
      halo.position.y = size * .72;
      visuals.add(halo);
      visuals.userData.halo = halo;
    }
    if (level >= 5) {
      const crown = new THREE.Mesh(new THREE.OctahedronGeometry(.34), spirit);
      crown.position.y = size * 1.03;
      visuals.add(crown);
      const light = new THREE.PointLight('#57f8ff', 3, size * 4);
      light.position.copy(crown.position);
      visuals.add(light);
    }
    root.add(visuals);
  }

  async showBattle(enemyState, army, progress = () => {}) {
    this.mode = 'battle';
    this.clearWorld();
    this.scene.fog.density = .016;
    this.camera.position.set(23, 27, 27);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
    const enemyBuildings = enemyState?.buildings || this.defaultEnemyBuildings();
    const biomeIndex = Number.isInteger(enemyState?.biomeIndex) ? enemyState.biomeIndex % BIOMES.length : this.battleSequence++ % BIOMES.length;
    const tasks = [this.addIsland(new THREE.Vector3(0, 0, 0), 29, 'เกาะศัตรู'), ...this.battleBiomeTasks(biomeIndex)];
    this.addGround(20, true);
    const skyCurrent = this.addSkyCurrent();
    const targets = [];
    const traps = [];
    let loaded = 0;
    for (const building of enemyBuildings) {
      const normalized = building.type ? building : this.fromNetworkBuilding(building, loaded);
      tasks.push(this.addBuilding(normalized, true).then(root => {
        targets.push(root);
        progress(++loaded / enemyBuildings.length);
      }));
    }
    const defenses = enemyState?.defenses || this.defaultBattleDefenses(Boolean(enemyState?.boss));
    for (const defense of defenses) {
      tasks.push(this.addBattleDefense(defense).then(root => {
        if (defense.type !== 'wall') traps.push(root);
      }));
    }
    await Promise.all(tasks);
    this.battle = {
      targets,
      traps,
      units: [],
      army: { ...army },
      initialBuildingHp: targets.reduce((sum, item) => sum + item.userData.maxHp, 0),
      percent: 0,
      stampCharge: GAME_RULES.battle.stamp.startingCharge,
      stampUntil: 0,
      stampUses: 0,
      skyCurrent,
      ended: false,
      defenseClock: 0,
      biome: BIOMES[biomeIndex].name,
      simulatedMs: 0,
      randomState: 0x9e3779b9 ^ this.battleSequence,
      heroSkillReadyAt: 0,
      heroSkillUses: 0
    };
    this.battleAccumulator = 0;
  }

  addSkyCurrent() {
    const start = new THREE.Vector3(-13, .16, -8.5);
    const end = new THREE.Vector3(10.5, .16, 7);
    const direction = end.clone().sub(start);
    const material = new THREE.MeshBasicMaterial({ color: '#55f4e5', transparent: true, opacity: .3, depthWrite: false, side: THREE.DoubleSide });
    const ribbon = new THREE.Mesh(new THREE.PlaneGeometry(GAME_RULES.battle.skyCurrent.width, direction.length()), material);
    ribbon.rotation.x = -Math.PI / 2;
    ribbon.rotation.z = -Math.atan2(direction.z, direction.x) + Math.PI / 2;
    ribbon.position.copy(start).add(end).multiplyScalar(.5);
    ribbon.userData.skyCurrent = true;
    this.worldRoot.add(ribbon);
    return { start, end, ribbon };
  }

  skyCurrentDistance(position) {
    const current = this.battle?.skyCurrent;
    if (!current) return Infinity;
    const point = new THREE.Vector2(position.x, position.z);
    const start = new THREE.Vector2(current.start.x, current.start.z);
    const line = new THREE.Vector2(current.end.x - current.start.x, current.end.z - current.start.z);
    const t = Math.max(0, Math.min(1, point.clone().sub(start).dot(line) / Math.max(.001, line.lengthSq())));
    return point.distanceTo(start.addScaledVector(line, t));
  }

  battleBiomeTasks(biomeIndex) {
    const biome = BIOMES[biomeIndex];
    const positions = [[-10, -7], [10, -7], [-10, 8], [10, 8], [-12, 1], [12, 1]];
    const modules = biome.modules.slice(0, positions.length).map((name, index) => {
      const [x, z] = positions[index];
      const size = name.includes('Cliff') ? 3.2 : 2.1;
      return this.addEnvironmentModel(name, new THREE.Vector3(x, .08, z), size, index * .8);
    });
    const monsterName = MONSTERS[this.battleSequence % MONSTERS.length];
    modules.push(this.addCatalogModel(assetUrl.monster(monsterName), new THREE.Vector3(9.5, .18, 10), 2.8, { fitHeight: true, rotation: -.7, color: '#b78a67' }));
    return modules;
  }

  defaultBattleDefenses(boss = false) {
    return [
      { type: 'wall', x: -3.2, z: -1.5, rotation: 0 },
      { type: 'wall', x: 3.2, z: -1.5, rotation: 0 },
      { type: 'spike', x: -7.2, z: -6.2 },
      { type: boss ? 'bomb' : 'spike', x: 7.2, z: -6.2 }
    ];
  }

  async addBattleDefense(defense) {
    const position = new THREE.Vector3(defense.x || 0, .12, defense.z || 0);
    const root = defense.type === 'wall'
      ? await this.addEnvironmentModel('ENV_WallStraight_L1.glb', position, 3.2, (defense.rotation || 0) * Math.PI / 2)
      : await this.addPropModel(defense.type === 'bomb' ? 'ENV_BombTrap_A.glb' : 'ENV_SpikeTrap_A.glb', position, defense.type === 'bomb' ? 1.8 : 2.1, defense.rotation || 0);
    root.userData.battleDefense = { type: defense.type, triggered: false, damage: defense.type === 'bomb' ? 130 : 58, radius: defense.type === 'bomb' ? 2.9 : 2.15 };
    return root;
  }

  fromNetworkBuilding(building, index) {
    const type = Object.keys(BUILDINGS).find(key => BUILDINGS[key].kind === building.kind) || 'arrow';
    return { id: `enemy-${index}`, type, level: building.level || 1, x: building.x, z: building.z };
  }

  defaultEnemyBuildings() {
    return [
      { id: 'e-hall', type: 'hall', level: 2, x: 0, z: 1 },
      { id: 'e-mint', type: 'mint', level: 2, x: -5.5, z: -2 },
      { id: 'e-timber', type: 'timber', level: 1, x: 5.5, z: -2 },
      { id: 'e-arrow-a', type: 'arrow', level: 2, x: -5, z: 4.7 },
      { id: 'e-arrow-b', type: 'arrow', level: 1, x: 5, z: 4.7 },
      { id: 'e-vault', type: 'vault', level: 1, x: 0, z: -5.2 },
      { id: 'e-bell', type: 'bell', level: 1, x: 0, z: 6.7 }
    ];
  }

  projectToScreen(position) {
    const projected = position.clone().project(this.camera);
    return {
      x: (projected.x * .5 + .5) * this.canvas.clientWidth,
      y: (-projected.y * .5 + .5) * this.canvas.clientHeight
    };
  }

  shake(power = .2, duration = 220) {
    this.shakeStrength = Math.max(this.shakeStrength, power);
    this.shakeUntil = Math.max(this.shakeUntil, performance.now() + duration);
  }

  impact(target, damage, critical = false, destroyed = false) {
    target.userData.hitPulse = performance.now() + 150;
    const point = target.position.clone().add(new THREE.Vector3(0, target.userData.definition.size * .58, 0));
    this.burst(point, critical ? '#ffe66e' : '#ff9f55', critical ? 11 : 5);
    this.shake(destroyed ? .34 : critical ? .19 : .07, destroyed ? 360 : 130);
    this.hooks.onCombatImpact?.({ screen: this.projectToScreen(point), damage: Math.round(damage), critical, destroyed, building: target.userData.definition.name });
  }

  destroyBuilding(target) {
    target.userData.hp = 0;
    target.userData.alive = false;
    target.userData.destroyAt = performance.now();
    target.userData.destroyStartY = target.position.y;
    const isHall = target.userData.state.type === 'hall';
    this.impact(target, 0, false, true);
    this.debris(target.position.clone().add(new THREE.Vector3(0, 1.2, 0)), isHall ? 18 : 10);
    if (isHall) {
      this.slowMotionUntil = performance.now() + 780;
      this.hooks.onCinematic?.({ kicker: 'CORE SHATTERED', title: 'ฐานหลักถูกทำลาย!' });
    }
    this.hooks.onSound?.('hit');
  }

  debris(position, count = 10) {
    const group = new THREE.Group();
    group.position.copy(position);
    const materials = [
      new THREE.MeshStandardMaterial({ color: '#d8c494', roughness: .85 }),
      new THREE.MeshStandardMaterial({ color: '#596a73', roughness: .9 }),
      new THREE.MeshStandardMaterial({ color: '#e4a84e', roughness: .65 })
    ];
    const pieces = [];
    for (let index = 0; index < count; index += 1) {
      const piece = new THREE.Mesh(new THREE.BoxGeometry(.14 + Math.random() * .22, .12 + Math.random() * .24, .14 + Math.random() * .22), materials[index % materials.length]);
      piece.userData.velocity = new THREE.Vector3((Math.random() - .5) * 6, 2.4 + Math.random() * 4.5, (Math.random() - .5) * 6);
      piece.userData.spin = new THREE.Vector3(Math.random() * 5, Math.random() * 5, Math.random() * 5);
      piece.castShadow = true;
      group.add(piece);
      pieces.push(piece);
    }
    this.worldRoot.add(group);
    const born = performance.now();
    const animate = () => {
      for (const piece of pieces) {
        piece.position.addScaledVector(piece.userData.velocity, .016);
        piece.userData.velocity.y -= 8.5 * .016;
        piece.rotation.x += piece.userData.spin.x * .016;
        piece.rotation.y += piece.userData.spin.y * .016;
      }
      if (performance.now() - born < 1100 && group.parent) requestAnimationFrame(animate);
      else this.worldRoot.remove(group);
    };
    animate();
  }

  async deployTroop(type, point, combatDefinition = null) {
    if (!this.battle || this.battle.ended || !TROOPS[type] || this.battle.army[type] <= 0) return false;
    this.battle.army[type] -= 1;
    const def = combatDefinition || TROOPS[type];
    const horizontal = new THREE.Vector2(point.x, point.z);
    if (horizontal.length() < 10.5) horizontal.setLength(10.5);
    if (horizontal.length() > 14) horizontal.setLength(14);
    // The generated Tripo skin matrices retain a Z-up rig even though the glTF
    // scene reports Y-up bounds. Normalize once before fitting and grounding it.
    let root;
    try { root = await this.model(assetUrl.character(def.model), 2.65, { fitHeight: true, rotateX: Math.PI / 2, color: '#d8a05c' }); }
    catch (error) { this.battle.army[type] += 1; throw error; }
    root.position.set(horizontal.x, .24, horizontal.y);
    root.scale.setScalar(.01);
    Object.assign(root.userData, { type, def, hp: def.hp, maxHp: def.hp, alive: true, attackClock: 0, spawnAt: performance.now(), motionState: '', currentBuffUntil: 0, firstStrikeUsed: false });
    this.prepareProceduralRig(root);
    this.playModelAnimation(root, 'idle');
    this.worldRoot.add(root);
    if (def.auroraTrail) {
      const aura = new THREE.Mesh(new THREE.RingGeometry(.55, .72, 32), new THREE.MeshBasicMaterial({ color: '#a878ff', transparent: true, opacity: .62, side: THREE.DoubleSide, depthWrite: false }));
      aura.rotation.x = -Math.PI / 2;
      aura.position.y = .04;
      aura.userData.auroraAura = true;
      root.add(aura);
    }
    this.battle.units.push(root);
    this.shockwave(root.position.clone(), '#83fff1', 1.5);
    this.hooks.onSound?.('deploy');
    return true;
  }

  playModelAnimation(root, state) {
    if (root.userData.motionState === state) return;
    root.userData.motionState = state;
    const animation = root.userData.animation;
    if (!animation?.actions?.size) return;
    const aliases = {
      idle: ['idle'], run: ['run', 'walk'], attack: ['attack', 'slash', 'shoot', 'cast'],
      hurt: ['hurt', 'hit'], defeat: ['defeat', 'fall', 'death'], victory: ['victory', 'celebrate']
    }[state] || [state];
    const match = [...animation.actions].find(([name]) => aliases.some(alias => name.includes(alias)));
    if (!match || animation.current === match[0]) return;
    for (const action of animation.actions.values()) action.fadeOut(.12);
    match[1].reset().fadeIn(.12).play();
    animation.current = match[0];
  }

  prepareProceduralRig(root) {
    const wanted = new Set(['L_Upperarm', 'R_Upperarm', 'L_Thigh', 'R_Thigh', 'L_Calf', 'R_Calf', 'Spine01', 'Spine02']);
    const bones = {};
    root.traverse(node => {
      if (node.isBone && wanted.has(node.name)) bones[node.name] = { node, base: node.rotation.clone() };
    });
    if (Object.keys(bones).length) root.userData.proceduralRig = bones;
  }

  animateProceduralMotion(root, now) {
    const content = root.children[0];
    if (!content?.userData.motionBasePosition) return;
    content.position.copy(content.userData.motionBasePosition);
    content.scale.copy(content.userData.motionBaseScale);
    content.rotation.z = 0;
    const rig = root.userData.proceduralRig;
    if (rig) {
      for (const part of Object.values(rig)) part.node.rotation.copy(part.base);
      if (rig.L_Upperarm) rig.L_Upperarm.node.rotation.z -= .92;
      if (rig.R_Upperarm) rig.R_Upperarm.node.rotation.z += .92;
      const stride = Math.sin(now * .012 + root.id);
      if (root.userData.motionState === 'run') {
        if (rig.L_Thigh) rig.L_Thigh.node.rotation.x += stride * .55;
        if (rig.R_Thigh) rig.R_Thigh.node.rotation.x -= stride * .55;
        if (rig.L_Upperarm) rig.L_Upperarm.node.rotation.x -= stride * .42;
        if (rig.R_Upperarm) rig.R_Upperarm.node.rotation.x += stride * .42;
        if (rig.Spine01) rig.Spine01.node.rotation.x += .12;
      } else if (root.userData.motionState === 'attack') {
        const swing = Math.max(0, Math.sin(now * .018));
        if (rig.L_Upperarm) rig.L_Upperarm.node.rotation.x -= swing * .9;
        if (rig.R_Upperarm) rig.R_Upperarm.node.rotation.x -= swing * .9;
        if (rig.Spine02) rig.Spine02.node.rotation.y += Math.sin(now * .018) * .16;
      }
    }
    if (root.userData.motionState === 'run') {
      content.position.y += Math.abs(Math.sin(now * .012 + root.id)) * .09;
      content.rotation.z = Math.sin(now * .01 + root.id) * .035;
    } else if (root.userData.motionState === 'attack') {
      content.position.y += Math.max(0, Math.sin(now * .018)) * .06;
      content.scale.z *= 1 + Math.max(0, Math.sin(now * .018)) * .06;
    } else {
      content.position.y += Math.sin(now * .0024 + root.id) * .025;
    }
  }

  useStamp() {
    if (!this.battle || this.battle.stampCharge < 100 || this.battle.ended || this.battle.stampUses >= GAME_RULES.battle.stamp.uses) return false;
    this.battle.stampCharge = 0;
    this.battle.stampUses += 1;
    this.battle.stampUntil = this.battle.simulatedMs + GAME_RULES.battle.stamp.durationMs;
    this.burst(new THREE.Vector3(0, 2, 0), '#51e8ff', 28);
    this.shockwave(new THREE.Vector3(0, .3, 0), '#7cfff1', 13);
    this.shake(.42, 520);
    this.hooks.onCinematic?.({ kicker: 'GALE STAMP', title: 'วายุพิทักษ์ตื่นขึ้น!' });
    return true;
  }

  useHeroSkill(type = 'runner') {
    const battle = this.battle;
    if (!battle || battle.ended || battle.simulatedMs < battle.heroSkillReadyAt || !battle.units.some(unit => unit.userData.alive)) return false;
    const units = battle.units.filter(unit => unit.userData.alive);
    const targets = battle.targets.filter(target => target.userData.alive);
    battle.heroSkillUses += 1;
    battle.heroSkillReadyAt = battle.simulatedMs + 22000;
    if (type === 'smith') {
      for (const unit of units) {
        unit.userData.hp = Math.min(unit.userData.maxHp, unit.userData.hp + unit.userData.maxHp * .28);
        unit.userData.barrierUntil = battle.simulatedMs + 6500;
        this.burst(unit.position.clone().add(new THREE.Vector3(0, 1, 0)), '#7deeff', 5);
      }
    } else if (type === 'lotus') {
      for (const unit of units) {
        unit.userData.hp = Math.min(unit.userData.maxHp, unit.userData.hp + unit.userData.maxHp * .48);
        this.burst(unit.position.clone().add(new THREE.Vector3(0, 1, 0)), '#9bffb0', 7);
      }
    } else {
      const ratio = type === 'ember' ? .12 : .075;
      const color = type === 'ember' ? '#ff9b54' : '#fff18b';
      for (const target of targets) {
        const damage = Math.max(35, target.userData.maxHp * ratio);
        target.userData.hp -= damage;
        this.impact(target, damage, type === 'runner', false);
        if (target.userData.hp <= 0) this.destroyBuilding(target);
      }
      this.shockwave(new THREE.Vector3(0, .4, 0), color, 10);
    }
    this.shake(.34, 430);
    this.hooks.onCinematic?.({ kicker: 'LEADER ART', title: type === 'smith' ? 'Aegis of Storm!' : type === 'lotus' ? 'Lotus Renewal!' : type === 'ember' ? 'Emberfall!' : 'Sky Volley!' });
    return true;
  }

  shockwave(position, color, endScale = 5) {
    const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: .9, side: THREE.DoubleSide, depthWrite: false });
    const ring = new THREE.Mesh(new THREE.RingGeometry(.7, .84, 64), material);
    ring.position.copy(position);
    ring.rotation.x = -Math.PI / 2;
    this.worldRoot.add(ring);
    const born = performance.now();
    const animate = () => {
      const progress = Math.min(1, (performance.now() - born) / 620);
      ring.scale.setScalar(.3 + progress * endScale);
      material.opacity = (1 - progress) * .85;
      if (progress < 1 && ring.parent) requestAnimationFrame(animate);
      else this.worldRoot.remove(ring);
    };
    animate();
  }

  updateBattle(delta) {
    const battle = this.battle;
    if (!battle || battle.ended) return;
    battle.simulatedMs += delta * 1000;
    const simNow = battle.simulatedMs;
    const now = performance.now();
    const battleRandom = () => {
      battle.randomState = (Math.imul(1664525, battle.randomState) + 1013904223) >>> 0;
      return battle.randomState / 4294967296;
    };
    for (const unit of battle.units) {
      if (!unit.userData.alive) continue;
      const age = now - unit.userData.spawnAt;
      if (age < 260) unit.scale.setScalar(Math.min(1, age / 260));
      if (this.skyCurrentDistance(unit.position) <= GAME_RULES.battle.skyCurrent.width * .5) unit.userData.currentBuffUntil = simNow + GAME_RULES.battle.skyCurrent.exitBuffMs;
      if (unit.userData.def.healer) {
        const wounded = battle.units.filter(friend => friend.userData.alive && friend !== unit && friend.userData.hp < friend.userData.maxHp);
        wounded.sort((a, b) => a.position.distanceToSquared(unit.position) - b.position.distanceToSquared(unit.position));
        const patient = wounded[0];
        if (patient && patient.position.distanceTo(unit.position) > 3.2) {
          const supportDirection = patient.position.clone().sub(unit.position); supportDirection.y = 0;
          unit.position.addScaledVector(supportDirection.normalize(), unit.userData.def.speed * delta);
          this.playModelAnimation(unit, 'run');
        }
        for (const friend of battle.units) {
          if (friend.userData.alive && friend !== unit && friend.position.distanceTo(unit.position) < 4.2) {
            friend.userData.hp = Math.min(friend.userData.maxHp, friend.userData.hp + 10 * (unit.userData.def.healingMultiplier || 1) * delta);
          }
        }
        if (patient) continue;
      }
      const aliveTargets = battle.targets.filter(target => target.userData.alive);
      if (!aliveTargets.length) continue;
      aliveTargets.sort((a, b) => targetScore(unit.userData.type, a.userData.state.type, a.position.distanceToSquared(unit.position)) - targetScore(unit.userData.type, b.userData.state.type, b.position.distanceToSquared(unit.position)));
      const target = aliveTargets[0];
      const direction = target.position.clone().sub(unit.position);
      direction.y = 0;
      const distance = direction.length();
      unit.rotation.y = Math.atan2(direction.x, direction.z);
      if (distance > 1.7 + target.userData.definition.size * .2) {
        this.playModelAnimation(unit, 'run');
        const stampBoost = simNow < battle.stampUntil ? 1.35 * (unit.userData.def.stampMultiplier || 1) : 1;
        const currentBoost = simNow < unit.userData.currentBuffUntil ? GAME_RULES.battle.skyCurrent.speedMultiplier : 1;
        const boost = stampBoost * currentBoost;
        unit.position.addScaledVector(direction.normalize(), unit.userData.def.speed * boost * delta);
        unit.position.y = .2 + Math.sin(now * .012 + unit.id) * .055;
      } else {
        this.playModelAnimation(unit, 'attack');
        unit.userData.attackClock += delta;
        unit.scale.y = 1 + Math.sin(now * .018) * .08;
        if (unit.userData.attackClock >= .56) {
          unit.userData.attackClock = 0;
          const critical = battleRandom() < (unit.userData.def.criticalChance || .14);
          let damage = unit.userData.def.damage * .56 * (critical ? 1.8 : 1);
          if (!unit.userData.firstStrikeUsed && unit.userData.type === 'smith' && target.userData.definition.defense) {
            damage *= unit.userData.def.firstDefenseStrikeMultiplier || 1;
            unit.userData.firstStrikeUsed = true;
          }
          target.userData.hp -= damage;
          this.impact(target, damage, critical, false);
          this.hooks.onSound?.(critical ? 'hit' : 'attack');
          if (target.userData.hp <= 0) this.destroyBuilding(target);
        }
      }
    }

    for (const trap of battle.traps || []) {
      const defense = trap.userData.battleDefense;
      if (!defense || defense.triggered) continue;
      const victims = battle.units.filter(unit => unit.userData.alive && unit.position.distanceTo(trap.position) <= defense.radius);
      if (!victims.length) continue;
      defense.triggered = true;
      for (const victim of victims) {
        const guarded = simNow < (victim.userData.barrierUntil || 0);
        const damage = Math.round(defense.damage * (guarded ? .35 : 1));
        victim.userData.hp -= damage;
        this.hooks.onCombatImpact?.({ screen: this.projectToScreen(victim.position.clone().add(new THREE.Vector3(0, 1.2, 0))), damage, critical: defense.type === 'bomb', hostile: true });
        if (victim.userData.hp <= 0) { victim.userData.alive = false; this.playModelAnimation(victim, 'defeat'); }
      }
      this.burst(trap.position.clone().add(new THREE.Vector3(0, .5, 0)), defense.type === 'bomb' ? '#ff754f' : '#c6f5ff', defense.type === 'bomb' ? 18 : 9);
      this.shake(defense.type === 'bomb' ? .32 : .16, 350);
      trap.scale.y *= .15;
      this.hooks.onSound?.('hit');
    }

    battle.defenseClock += delta;
    if (battle.defenseClock >= .7) {
      battle.defenseClock = 0;
      const defenders = battle.targets.filter(target => target.userData.alive && target.userData.definition.defense);
      for (const defender of defenders) {
        const victims = battle.units.filter(unit => unit.userData.alive && unit.position.distanceTo(defender.position) < 11);
        if (!victims.length) continue;
        victims.sort((a, b) => a.position.distanceToSquared(defender.position) - b.position.distanceToSquared(defender.position));
        const victim = victims[0];
        const guarded = simNow < (victim.userData.barrierUntil || 0);
        victim.userData.hp -= defender.userData.definition.defense * .7 * (guarded ? .35 : 1);
        this.projectile(defender.position, victim.position);
        this.hooks.onCombatImpact?.({ screen: this.projectToScreen(victim.position.clone().add(new THREE.Vector3(0, 1.4, 0))), damage: Math.round(defender.userData.definition.defense * .7), critical: false, hostile: true });
        if (victim.userData.hp <= 0) {
          this.playModelAnimation(victim, 'defeat');
          victim.userData.alive = false;
          window.setTimeout(() => { victim.visible = false; }, 260);
          this.burst(victim.position, '#9ce4ff', 8);
        }
      }
    }
    const hp = battle.targets.reduce((sum, target) => sum + Math.max(0, target.userData.hp), 0);
    const nextPercent = Math.min(100, Math.round((1 - hp / battle.initialBuildingHp) * 100));
    if (nextPercent > battle.percent && battle.stampUses < GAME_RULES.battle.stamp.uses) battle.stampCharge = Math.min(100, battle.stampCharge + (nextPercent - battle.percent) * GAME_RULES.battle.stamp.chargePerDestruction);
    battle.percent = nextPercent;
    const hallAlive = battle.targets.some(target => target.userData.state.type === 'hall' && target.userData.alive);
    const marks = (battle.percent >= 50 ? 1 : 0) + (!hallAlive ? 1 : 0) + (battle.percent >= 100 ? 1 : 0);
    this.hooks.onBattleUpdate?.({ percent: battle.percent, marks, charge: Math.round(battle.stampCharge), aliveUnits: battle.units.filter(u => u.userData.alive).length, skillReadyIn: Math.max(0, Math.ceil((battle.heroSkillReadyAt - battle.simulatedMs) / 1000)) });
    if (battle.percent >= 100) this.hooks.onBattleComplete?.({ percent: 100, marks: 3 });
    else if (battle.units.length && !battle.units.some(unit => unit.userData.alive) && !Object.values(battle.army).some(Boolean)) this.hooks.onBattleComplete?.({ percent: battle.percent, marks });
  }

  async projectile(from, to) {
    const projectileName = PROJECTILES[this.projectileSequence++ % PROJECTILES.length];
    const orb = await this.model(assetUrl.projectile(projectileName), .52, { color: '#fff47e' });
    orb.position.copy(from).add(new THREE.Vector3(0, 2, 0));
    this.worldRoot.add(orb);
    this.hooks.onSound?.('attack');
    const start = orb.position.clone();
    const born = performance.now();
    const animate = () => {
      const t = Math.min(1, (performance.now() - born) / 260);
      orb.position.lerpVectors(start, to.clone().add(new THREE.Vector3(0, 1, 0)), t);
      if (t < 1 && orb.parent) requestAnimationFrame(animate);
      else this.worldRoot.remove(orb);
    };
    animate();
  }

  burst(position, color, count) {
    const group = new THREE.Group();
    group.position.copy(position);
    const material = new THREE.MeshBasicMaterial({ color, transparent: true });
    const particles = [];
    for (let i = 0; i < count; i += 1) {
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(.09 + Math.random() * .12, 6, 6), material);
      mesh.userData.velocity = new THREE.Vector3((Math.random() - .5) * 5, 2 + Math.random() * 4, (Math.random() - .5) * 5);
      group.add(mesh);
      particles.push(mesh);
    }
    this.worldRoot.add(group);
    const born = performance.now();
    const animate = () => {
      const delta = .016;
      for (const particle of particles) {
        particle.position.addScaledVector(particle.userData.velocity, delta);
        particle.userData.velocity.y -= 7 * delta;
      }
      material.opacity = Math.max(0, 1 - (performance.now() - born) / 850);
      if (material.opacity > 0 && group.parent) requestAnimationFrame(animate);
      else this.worldRoot.remove(group);
    };
    animate();
  }

  endBattle() {
    if (this.battle) this.battle.ended = true;
  }

  updateWorldMood(now) {
    const cycle = (now / 360000) % 1;
    const twilight = cycle > .56;
    const nightMix = twilight ? Math.sin(((cycle - .56) / .44) * Math.PI) * .48 : 0;
    const day = new THREE.Color('#8edff0');
    const evening = new THREE.Color('#7d86cf');
    this.scene.background.copy(day).lerp(evening, nightMix);
    this.scene.fog.color.copy(new THREE.Color('#bcebf1').lerp(new THREE.Color('#777eaf'), nightMix));
    this.sun.color.copy(new THREE.Color('#fff4dc').lerp(new THREE.Color('#ffb37d'), nightMix));
    this.sun.intensity = 3.4 - nightMix * 1.35;
    this.rim.intensity = 1.2 + nightMix * 1.8;
    this.hemi.intensity = 2.15 - nightMix * .6;
    const mood = nightMix > .16 ? 'twilight' : 'day';
    if (mood !== this.lastMood) {
      this.lastMood = mood;
      this.hooks.onWorldMood?.(mood);
    }
  }

  updateAmbient(now, delta) {
    for (const object of this.ambientObjects) {
      const ambient = object.userData.ambient;
      if (!ambient || !object.parent) continue;
      const wave = Math.sin(now * .0015 + ambient.phase);
      if (ambient.type === 'water') {
        object.position.y = ambient.baseY + wave * .035;
        object.rotation.y = ambient.baseRotation + now * .00016;
      } else if (ambient.type === 'banner') {
        object.rotation.z = wave * .035;
      } else if (ambient.type === 'magic') {
        object.position.y = ambient.baseY + wave * .045;
        object.rotation.y = ambient.baseRotation + wave * .08;
        const glow = object.children.find(child => child.userData.ambientGlow);
        if (glow) glow.intensity = .45 + (wave + 1) * .18;
      } else if (ambient.type === 'foliage') {
        object.rotation.z = wave * .018;
        object.rotation.x = Math.cos(now * .0012 + ambient.phase) * .012;
      } else if (ambient.type === 'spirit') {
        const angle = now * .00011 + ambient.phase;
        object.position.x = ambient.centerX + Math.cos(angle) * ambient.radius;
        object.position.z = ambient.centerZ + Math.sin(angle) * ambient.radius;
        object.position.y = ambient.baseY + Math.sin(now * .002 + ambient.phase) * .32;
        object.material.opacity = .58 + (wave + 1) * .17;
      }
    }
    for (const villager of this.villagers) {
      if (!villager.parent) continue;
      const data = villager.userData.villager;
      data.phase += data.speed * delta;
      const next = new THREE.Vector3(data.centerX + Math.cos(data.phase) * data.radius, .035, data.centerZ + Math.sin(data.phase) * data.radius);
      const direction = next.clone().sub(villager.position);
      villager.rotation.y = Math.atan2(direction.x, direction.z);
      villager.position.lerp(next, Math.min(1, delta * 2.2));
      this.animateProceduralMotion(villager, now);
    }
    for (const target of this.battle?.targets || []) {
      if (target.userData.hitPulse) {
        const remaining = target.userData.hitPulse - now;
        const punch = remaining > 0 ? 1 + Math.sin((150 - remaining) * .05) * .035 : 1;
        const levelScale = 1 + (target.userData.state.level - 1) * .075;
        target.scale.setScalar(levelScale * punch);
        if (remaining <= 0) target.userData.hitPulse = 0;
      }
      if (target.userData.destroyAt) {
        const progress = Math.min(1, (now - target.userData.destroyAt) / 620);
        target.rotation.z = Math.sin(progress * Math.PI) * .18;
        target.position.y = target.userData.destroyStartY - progress * 1.35;
        target.scale.multiplyScalar(1 - delta * 1.8);
        if (progress >= 1) target.visible = false;
      }
    }
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    const now = performance.now();
    const rawDelta = Math.min(.05, this.clock.getDelta());
    this.updateAdaptiveQuality(rawDelta);
    const delta = rawDelta * (now < this.slowMotionUntil ? .28 : 1);
    this.controls.update();
    for (const mixer of this.mixers) mixer.update(delta);
    if (this.mode === 'battle') {
      this.battleAccumulator = Math.min(.2, this.battleAccumulator + delta);
      while (this.battleAccumulator >= 1 / 30) {
        this.updateBattle(1 / 30);
        this.battleAccumulator -= 1 / 30;
      }
      for (const unit of this.battle?.units || []) if (unit.userData.alive) this.animateProceduralMotion(unit, now);
    }
    this.updateWorldMood(now);
    this.updateAmbient(now, delta);
    const time = now * .00008;
    for (const object of this.scene.children) {
      if (object.userData.cloud) {
        object.position.x += object.userData.drift * delta;
        if (object.position.x > 55) object.position.x = -55;
        object.position.y += Math.sin(time + object.id) * .0008;
      }
    }
    if (this.highlight) {
      this.highlight.rotation.z += delta * .35;
      this.highlight.material.opacity = .72 + Math.sin(now * .006) * .2;
    }
    const cameraBase = this.camera.position.clone();
    if (now < this.shakeUntil) {
      const fade = Math.max(0, (this.shakeUntil - now) / 360);
      this.camera.position.x += (Math.random() - .5) * this.shakeStrength * fade;
      this.camera.position.y += (Math.random() - .5) * this.shakeStrength * fade;
      this.camera.position.z += (Math.random() - .5) * this.shakeStrength * fade;
    } else this.shakeStrength = 0;
    this.renderer.render(this.scene, this.camera);
    this.camera.position.copy(cameraBase);
  }
}
